import { TrashAltIcon } from "assets/icons";
import { Category, useGetAllCategories } from "modules/categories";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { IconButton } from "ui";

import {
  ENRICHMENT_MODE_COLORS,
  ENRICHMENT_MODE_LABELS
} from "../../constants";
import { flattenCategoryTree } from "../../helpers";
import {
  CategoryEnrichmentProfile,
  EEnrichmentMode
} from "../../recommendation.types";
import { Badge } from "../Badge";

import styles from "./EnrichmentProfileList.module.scss";

interface EnrichmentProfileListProps {
  profiles: CategoryEnrichmentProfile[];
  isLoading?: boolean;
  onDelete: (profile: CategoryEnrichmentProfile) => void;
}

const pathOf = (category: Category, byId: Map<string, Category>): string => {
  const parts = [category.name];
  let current = category.parentCategoryId
    ? byId.get(category.parentCategoryId)
    : undefined;

  while (current) {
    parts.unshift(current.name);
    current = current.parentCategoryId
      ? byId.get(current.parentCategoryId)
      : undefined;
  }

  return parts.join(" › ");
};

const EnrichmentProfileList = ({
  profiles,
  isLoading,
  onDelete
}: EnrichmentProfileListProps) => {
  const { t } = useTranslation();

  const { categories, isLoading: isCategoriesLoading } = useGetAllCategories();

  const { byId, childrenOf } = useMemo(() => {
    const flat = flattenCategoryTree(categories);
    const idMap = new Map(flat.map((category) => [category.id, category]));
    const children = new Map<string, Category[]>();

    flat.forEach((category) => {
      if (!category.parentCategoryId) {
        return;
      }

      const siblings = children.get(category.parentCategoryId) ?? [];
      siblings.push(category);
      children.set(category.parentCategoryId, siblings);
    });

    return { byId: idMap, childrenOf: children };
  }, [categories]);

  const configuredIds = useMemo(
    () => new Set(profiles.map((profile) => profile.categoryId)),
    [profiles]
  );

  // How many categories below this one actually follow the rule. A descendant
  // that carries its own rule overrides it, and takes its own subtree with it.
  const inheritorCount = (categoryId: string): number => {
    let count = 0;

    const walk = (parentId: string) => {
      (childrenOf.get(parentId) ?? []).forEach((child) => {
        if (configuredIds.has(child.id)) {
          return;
        }

        count += 1;
        walk(child.id);
      });
    };

    walk(categoryId);

    return count;
  };

  if (isLoading || isCategoriesLoading) {
    return <Skeleton count={3} height={70} borderRadius={14} />;
  }

  if (!profiles.length) {
    return (
      <p className={styles.empty}>
        {t(
          "Nothing configured — every category is tagged from its photo with the general attribute set."
        )}
      </p>
    );
  }

  return (
    <div className={styles.list}>
      {profiles.map((profile) => {
        const category = byId.get(profile.categoryId);
        const inheritors = inheritorCount(profile.categoryId);

        return (
          <div key={profile.id} className={styles.row}>
            <div className={styles.row__main}>
              <span className={styles.row__path}>
                {category ? pathOf(category, byId) : t("Unknown category")}
              </span>

              <span className={styles.row__inherit}>
                {inheritors
                  ? `${inheritors} ${
                      inheritors === 1
                        ? t("category inherits this")
                        : t("categories inherit this")
                    }`
                  : t("no descendants")}
              </span>
            </div>

            <span className={styles.row__profile}>
              {profile.mode === EEnrichmentMode.None
                ? "—"
                : (profile.profileKey ?? t("General"))}
            </span>

            <Badge
              label={ENRICHMENT_MODE_LABELS[profile.mode]}
              color={ENRICHMENT_MODE_COLORS[profile.mode]}
            />

            <IconButton
              className={styles.row__delete}
              icon={<TrashAltIcon fill="#ff0033" width={18} height={18} />}
              onClick={() => onDelete(profile)}
            />
          </div>
        );
      })}
    </div>
  );
};

export default EnrichmentProfileList;
